import { useQuery } from "@tanstack/react-query";
import api, { fmtOdds } from "../lib/api";
import { BookLinks, BOOK_SHORT, useBookLinks, trackBookClick } from "../components/BookLinks";
import { Clock } from "lucide-react";

function PriceLink({ odds, book, bold, testid, urls }) {
  if (odds == null) return null;
  const url = book ? urls[book] : null;
  const inner = <>{fmtOdds(odds)}{book && <sup className="text-[8px] text-neutral-500 ml-0.5 font-sans">{BOOK_SHORT[book] || book}</sup>}</>;
  if (!url) return <b className={bold ? "text-[#22c55e]" : "text-neutral-300"}>{inner}</b>;
  return (
    <a href={url} target="_blank" rel="noreferrer" title={`Best price at ${book} — opens sportsbook`} data-testid={testid} onClick={() => trackBookClick(book)}
      className={`font-bold underline decoration-dotted underline-offset-2 hover:text-[#a855f7] transition-colors duration-200 ${bold ? "text-[#22c55e]" : "text-neutral-300"}`}>
      {inner}
    </a>
  );
}

function WinBar({ away, home, awayPct, homePct }) {
  const homeFav = homePct >= awayPct;
  return (
    <div data-testid="winbar">
      <div className="flex justify-between text-[11px] font-data mb-1">
        <span className={!homeFav ? "text-[#22c55e] font-bold" : "text-neutral-400"}>{away} {awayPct}%</span>
        <span className={homeFav ? "text-[#22c55e] font-bold" : "text-neutral-400"}>{homePct}% {home}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden flex bg-white/10">
        <div className={`h-full ${!homeFav ? "bg-[#22c55e]" : "bg-white/25"}`} style={{ width: `${awayPct}%` }} />
        <div className={`h-full ${homeFav ? "bg-[#22c55e]" : "bg-white/25"}`} style={{ width: `${homePct}%` }} />
      </div>
    </div>
  );
}

export default function GameLines() {
  const { data, isLoading } = useQuery({ queryKey: ["gameLines"], queryFn: () => api.get("/game-lines").then((r) => r.data), refetchInterval: 300000 });
  const { urls } = useBookLinks();
  const games = data?.games || [];
  const modeled = games.length > 0 && games[0].source === "model";

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">Game <span className="gold-text">Lines</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Every game on today's slate — starting pitchers, moneylines, run lines and totals, with the model's win probability for each side.</p>
        {modeled && <p className="text-[11px] text-[#f59e0b] mt-1">Books haven't posted lines yet — showing model-estimated numbers until they do.</p>}
      </div>

      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Shopping the books...</div>}
      {!isLoading && games.length === 0 && (
        <div className="card-surface rounded-xl p-10 text-center" data-testid="lines-empty">
          <p className="text-neutral-400">No games with lines today. Check back on the next game day.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 fade-up-1">
        {games.map((g, i) => {
          const homeFav = g.win_pct_home >= g.win_pct_away;
          const fav = homeFav ? g.home : g.away;
          return (
            <div key={i} className="card-surface hover:border-[#a855f7]/40 rounded-xl p-4 transition-colors duration-200" data-testid={`line-card-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-lg font-bold leading-tight">{g.away} <span className="text-neutral-500 text-sm">@</span> {g.home}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    {g.away_pitcher?.name || "TBD"} <span className="text-neutral-700">vs</span> {g.home_pitcher?.name || "TBD"}
                  </div>
                </div>
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#a855f7] shrink-0"><Clock size={11} />{g.time_et || "TBD"}</span>
              </div>

              <div className="mt-3"><WinBar away={g.away} home={g.home} awayPct={g.win_pct_away} homePct={g.win_pct_home} /></div>
              <div className="text-[11px] text-neutral-500 mt-1" data-testid={`line-fav-${i}`}>
                Model likes <b className="text-[#22c55e]">{fav}</b> to win ({homeFav ? g.win_pct_home : g.win_pct_away}%)
              </div>

              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div className="bg-black/40 border border-white/10 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Moneyline</div>
                  <div className="font-data text-xs sm:text-sm mt-1">
                    <div>{g.away.split(" ").slice(-1)[0]} <PriceLink odds={g.ml_away} book={g.ml_away_book} bold={!homeFav} testid={`ml-away-link-${i}`} urls={urls} /></div>
                    <div>{g.home.split(" ").slice(-1)[0]} <PriceLink odds={g.ml_home} book={g.ml_home_book} bold={homeFav} testid={`ml-home-link-${i}`} urls={urls} /></div>
                  </div>
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Run Line</div>
                  {g.spread_home == null ? (
                    <div className="font-data text-xs text-neutral-600 mt-2" data-testid={`rl-pending-${i}`}>Not posted yet</div>
                  ) : (
                  <div className="font-data text-xs sm:text-sm mt-1">
                    <div>{g.away.split(" ").slice(-1)[0]} {g.spread_away > 0 ? `+${g.spread_away}` : g.spread_away} <PriceLink odds={g.spread_away_price} book={g.spread_away_book} testid={`rl-away-link-${i}`} urls={urls} /></div>
                    <div>{g.home.split(" ").slice(-1)[0]} {g.spread_home > 0 ? `+${g.spread_home}` : g.spread_home} <PriceLink odds={g.spread_home_price} book={g.spread_home_book} testid={`rl-home-link-${i}`} urls={urls} /></div>
                  </div>
                  )}
                </div>
                <div className="bg-black/40 border border-white/10 rounded p-2">
                  <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Total</div>
                  {g.total == null ? (
                    <div className="font-data text-xs text-neutral-600 mt-2" data-testid={`total-pending-${i}`}>Not posted yet</div>
                  ) : (
                  <div className="font-data text-xs sm:text-sm mt-1">
                    <div>O {g.total} <PriceLink odds={g.over_price} book={g.over_book} testid={`total-over-link-${i}`} urls={urls} /></div>
                    <div>U {g.total} <PriceLink odds={g.under_price} book={g.under_book} testid={`total-under-link-${i}`} urls={urls} /></div>
                  </div>
                  )}
                </div>
              </div>
              {g.books > 0 && <div className="text-[10px] text-neutral-600 mt-2">Best price across {g.books} books — tap any price to open that sportsbook</div>}
            </div>
          );
        })}
      </div>

      {games.length > 0 && (
        <div className="card-surface rounded-xl p-4 fade-up-2">
          <BookLinks title="Ready to bet a game? Open your book" testid="lines-book-links" />
        </div>
      )}
    </div>
  );
}
